import { redirect, type LoaderFunctionArgs } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { useSubscriptionStore } from '@/features/auth/stores/subscription.store';
import { useFormBuilderStore } from '@/features/form-builder/store/form-builder.store';
import { useMappingsStore } from '@/features/form-builder/store/mappings.store';
import { useCrmStore } from '@/features/form-builder/store/crm.store';
import { useLogoStore } from '@/features/application/store/logo.store';
import { logger } from '@/features/help/utils/logger';

/**
 * Helper function to wait for app user data to be loaded from the backend
 */
const waitForAppUserData = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const MAX_WAIT_TIME = 10000; // 10 seconds
    const CHECK_INTERVAL = 100;
    const MIN_WAIT_TIME = 1000; // Give at least 1 second for fetchAppUser to complete
    let elapsedTime = 0;

    const checkAppUser = () => {
      const { appUser, isLoading, error } = useAuthStore.getState();

      // Case 1: Success - we have a user and loading is complete.
      if (appUser) {
        logger.info('DashboardLoader', 'App user data loaded successfully', {
          userId: appUser.userId,
          crmProvider: appUser.crmProvider,
        });
        resolve();
        return;
      }

      // Case 2: An error occurred during fetching.
      if (error) {
        logger.error('DashboardLoader', 'Failed to load app user data during wait', { error });
        reject(new Error(error));
        return;
      }

      // Case 3: Timeout exceeded.
      if (elapsedTime >= MAX_WAIT_TIME) {
        const errorMessage = `Timed out waiting for app user data after ${
          MAX_WAIT_TIME / 1000
        } seconds.`;
        logger.error('DashboardLoader', errorMessage);
        reject(new Error(errorMessage));
        return;
      }

      // Case 4: Loading finished, but no user and no error - but only reject after minimum wait time
      // This gives fetchAppUser time to complete since there's a race condition between
      // handleUserSignedIn setting isLoading:false and fetchAppUser completing
      if (!isLoading && !appUser && elapsedTime >= MIN_WAIT_TIME) {
        const errorMessage = 'App user data not available after loading and no error was reported.';
        logger.error('DashboardLoader', errorMessage);
        reject(new Error(errorMessage));
        return;
      }

      // Otherwise, continue waiting
      elapsedTime += CHECK_INTERVAL;
      setTimeout(checkAppUser, CHECK_INTERVAL);
    };

    checkAppUser();
  });
};

/**
 * Loads the heavy core data for the dashboard. This is the "heavy" part that we defer.
 * This only includes data fetching operations (org settings, objects, fields, mappings, etc.)
 * All authentication, subscription, and CRM connection checks happen in the blocking loader.
 */
const loadCoreDashboardData = async () => {
  logger.debug('DashboardLoader', 'Starting deferred heavy data loading');

  // Load Core Dashboard Data
  logger.debug('DashboardLoader', 'Loading core dashboard data');
  try {
    // Load org-wide settings first
    await useCrmStore.getState().fetchAddressPicklistSetting();

    // Fetch supported objects
    await useCrmStore.getState().fetchSupportedCrmObjects();

    // Fetch all fields for those objects
    await useCrmStore.getState().fetchAllSupportedObjectFields();

    // Fetch mapping details
    await useMappingsStore.getState().fetchAllMappingDetails();

    // Autoselect the first mapping if available
    const { mappingSummaries } = useMappingsStore.getState();
    if (mappingSummaries.length > 0) {
      useMappingsStore.getState().setCurrentFieldMappingId(mappingSummaries[0].mapping_id);
      useFormBuilderStore.getState().loadStructureForCurrentMapping();
    } else {
      useMappingsStore.getState().setCurrentFieldMappingId(null);
    }

    // Fetch and preload company logo
    await useLogoStore.getState().fetchLogo();
    const { preloadLogoImage } = useLogoStore.getState();
    if (preloadLogoImage) {
      await preloadLogoImage();
    }

    // Mark dashboard as initialized
    useFormBuilderStore.getState().setDashboardInitialized(true);

    logger.success('DashboardLoader', 'Heavy dashboard data loaded successfully');

    return {
      dashboardInitialized: true,
    };
  } catch (error) {
    logger.error('DashboardLoader', 'Failed to load heavy dashboard data', { error });
    // Re-throw the error so the Await component's errorElement can catch it.
    throw error;
  }
};

/**
 * Dashboard loader function that handles authentication, subscription checks,
 * CRM connection verification, and data fetching before rendering dashboard components
 */
export const dashboardLoader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const pathname = url.pathname;

  logger.info('DashboardLoader', 'Starting dashboard loader', { pathname });

  // Step 1: Check Authentication
  const { isLoading: isAuthLoading } = useAuthStore.getState();

  if (isAuthLoading) {
    logger.debug('DashboardLoader', 'Auth still loading, waiting...');
    // Wait for auth to complete
    await new Promise((resolve) => {
      const checkAuth = () => {
        const { isLoading } = useAuthStore.getState();
        if (!isLoading) {
          resolve(void 0);
        } else {
          setTimeout(checkAuth, 100);
        }
      };
      checkAuth();
    });
  }

  const { isAuthenticated: finalAuthState, firebaseUser: finalUser } = useAuthStore.getState();

  if (!finalAuthState || !finalUser) {
    logger.info('DashboardLoader', 'User not authenticated, redirecting to login');
    return redirect('/login');
  }

  if (!finalUser.emailVerified) {
    logger.info('DashboardLoader', 'Email not verified, redirecting to verification page');
    return redirect('/verify-email');
  }

  // Step 2: Wait for App User Data (fixes race condition)
  logger.debug('DashboardLoader', 'Waiting for app user data to load');
  try {
    await waitForAppUserData();
  } catch (error) {
    logger.error('DashboardLoader', 'Failed to load app user data', { error });
    return redirect('/login');
  }

  const { appUser } = useAuthStore.getState();

  if (!appUser) {
    logger.error('DashboardLoader', 'App user data not available after wait');
    return redirect('/login');
  }

  // Step 3: Check Subscription Status (blocking)
  logger.debug('DashboardLoader', 'Checking subscription status');
  try {
    await useSubscriptionStore.getState().checkSubscription();
    const { isSubscribed, freeTrialRemaining } = useSubscriptionStore.getState();

    if (!isSubscribed && freeTrialRemaining <= 0) {
      logger.warn('DashboardLoader', 'No valid subscription, trial exceeded');
      // The component will handle rendering the upgrade modal, but we continue loading
    }
  } catch (error) {
    logger.error('DashboardLoader', 'Failed to check subscription', { error });
    // Continue loading - the component can handle subscription errors
  }

  // Step 4: Check CRM Provider Selection
  if (!appUser.crmProvider) {
    logger.info('DashboardLoader', 'No CRM provider selected, redirecting to provider selection');
    return redirect('/select-provider');
  }

  // Step 5: Check CRM Connection (blocking)
  if (!pathname.includes('/crm/callback')) {
    logger.debug('DashboardLoader', 'Checking CRM connection');
    try {
      const isCrmConnected = await useAuthStore.getState().checkCrmConnection();

      if (!isCrmConnected) {
        const crmProvider = appUser.crmProvider;
        logger.info('DashboardLoader', 'CRM not connected, initiating OAuth', {
          provider: crmProvider,
        });

        if (crmProvider) {
          // This will trigger a redirect, and the loader ends here.
          await useAuthStore.getState().initiateOAuth(crmProvider);
          // Return a redirect to prevent any UI from rendering
          return redirect('/dashboard'); // Placeholder - OAuth will redirect before this is processed
        } else {
          logger.error('DashboardLoader', 'CRM provider not found, cannot initiate OAuth');
          return redirect('/select-provider');
        }
      }
    } catch (error: any) {
      logger.error('DashboardLoader', 'Failed to check CRM connection', { error });
      return redirect('/select-provider');
    }
  }

  // Step 6: Defer only the heavy data loading
  logger.debug('DashboardLoader', 'Deferring heavy data loading');
  const deferredDataPromise = loadCoreDashboardData();

  return {
    appUser,
    coreDataPromise: deferredDataPromise,
  };
};
